import type {
  AvailabilityProfile,
  AvailabilityImportPreviewRow,
  AvailabilityImportRematchRow,
} from '@ooh/contracts';
import type { InterpretedRow } from '../components/interpreted-rows-table';
import { deriveInterpretedRows, pickRawIdentifier } from './derive-interpreted-rows';

/** Single key-by-rowNumber index over the debounced rematch response —
 * built once per response and shared by `mergeRematchedRows` and
 * `buildDisplayRows` so the same Map is never rebuilt twice per render.
 * Extracted out of `preview-phase.tsx` (CU-86cbafj4j) to keep that file
 * under the repo's per-file line-count budget. */
export type RematchByRowNumber = Map<number, AvailabilityImportRematchRow>;

export function indexRematchedRows(
  rematchedRows: AvailabilityImportRematchRow[] | undefined,
): RematchByRowNumber {
  return new Map((rematchedRows ?? []).map((r) => [r.rowNumber, r]));
}

/** Overlays the debounced rematch response onto the initial preview rows —
 * a row not present in the rematch index (not yet rematched, or unaffected)
 * keeps its original `matchedUnitId`/`matchMethod`. */
export function mergeRematchedRows(
  baseRows: AvailabilityImportPreviewRow[],
  rematchByRowNumber: RematchByRowNumber,
): AvailabilityImportPreviewRow[] {
  if (rematchByRowNumber.size === 0) return baseRows;
  return baseRows.map((row) => {
    const override = rematchByRowNumber.get(row.rowNumber);
    if (!override) return row;
    return { ...row, matchedUnitId: override.matchedUnitId, matchMethod: override.matchMethod };
  });
}

export function buildDisplayRows(
  profile: AvailabilityProfile,
  sampleRows: Record<string, string>[],
  baseRows: AvailabilityImportPreviewRow[],
  rematchByRowNumber: RematchByRowNumber,
): InterpretedRow[] {
  if (profile.timeEncoding === 'month' || profile.timeEncoding === 'half_month') {
    return baseRows.map((r, i) => ({
      id: `base-${r.rowNumber}-${i}`,
      unit:
        r.matchedUnitId !== null
          ? `#${r.matchedUnitId}`
          : pickRawIdentifier(profile, sampleRows[r.rowNumber - 1] ?? {}) || `#${r.rowNumber}`,
      matched: r.matchedUnitId !== null,
      period: `${r.period.start} – ${r.period.end}`,
      status: r.state,
    }));
  }
  // Match state per sample row: the debounced rematch response wins when it
  // covers the row — it is computed against the CURRENT edited profile.
  // Falling back to baseRows only would silently drop every rematch result
  // whenever the INITIAL preview produced zero interpreted rows (e.g. an
  // auto-resolved period_label_map profile whose periodLabelMap starts
  // empty — CU-86canaqmw: every row stayed "Niedopasowany" no matter what
  // the operator corrected, because `rows: []` had nothing to overlay onto).
  const matchSeeds = sampleRows.map((_, i) => {
    const rematched = rematchByRowNumber.get(i + 1);
    if (rematched) return { matched: rematched.matchedUnitId !== null };
    return {
      matched: baseRows.some((r) => r.rowNumber === i + 1 && r.matchedUnitId !== null),
    };
  });
  return deriveInterpretedRows(profile, sampleRows, matchSeeds);
}

/** Every row failed to match a unit — the mapping itself is wrong (e.g. a
 * remembered mapping predating address/city support), not just a
 * temporarily-empty required field (CU-86cbafj4j).
 *
 * `sampleRowCount` distinguishes two very different reasons `rows` can be
 * empty (QA bugfix round 2, CU-86cb1npz2): a genuinely empty file (nothing
 * to import yet — `sampleRowCount === 0`, not a mapping problem) versus a
 * file that DOES have rows but produced zero interpreted rows anyway — the
 * month/half_month branch of `buildDisplayRows` maps 1:1 over the server's
 * own `initialData.rows`, which is computed once against the (possibly
 * still-wrong) auto-resolved profile and can be empty independent of the
 * operator's own periodColumns edits. That second case is exactly the
 * "everything looks fine but nothing would import" state the backend's own
 * `ALL_ROWS_UNMATCHED` confirm-time guard exists to catch — this check is
 * the client-side first line of defense before that request is ever sent,
 * so treating it as vacuously "not all unmatched" (the previous
 * `rows.length > 0 && ...` check) let it slip through undetected. */
export function computeAllRowsUnmatched(rows: InterpretedRow[], sampleRowCount: number): boolean {
  if (rows.length === 0) return sampleRowCount > 0;
  return rows.every((r) => !r.matched);
}
